// ============================================================
// TaskFlow OS - Operational Node (React Flow Custom Node)
// Liquid Glass Design with Status Animations
// ============================================================

import React, { memo, useMemo } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { NodeStatus, PriorityLevel } from '../../lib/types';
import { NODE_STATUS_LABELS, PRIORITY_LABELS, NODE_STATUS_COLORS, PRIORITY_COLORS, NODE_STATUS_ANIMATIONS } from '../../lib/constants';
import { IconClock, IconUser } from '../../icons/SvgIcons';

interface OperationalNodeProps {
  data: {
    label: string;
    status: NodeStatus;
    priority: PriorityLevel;
    assigneeName?: string;
    assigneeAvatar?: string;
    description?: string;
    dueDate?: string;
    tags?: string[];
    onEdit?: () => void;
    onStatusChange?: (status: NodeStatus) => void;
  };
  selected: boolean;
}

const OperationalNodeComponent: React.FC<OperationalNodeProps> = ({ data, selected }) => {
  const statusColor = NODE_STATUS_COLORS[data.status];
  const priorityColor = PRIORITY_COLORS[data.priority];
  const animation = NODE_STATUS_ANIMATIONS[data.status];

  const ringClass = useMemo(() => {
    switch (data.status) {
      case 'in_progress': return 'node-ring-progress';
      case 'completed': return 'node-ring-completed';
      case 'error': return 'node-ring-error';
      case 'waiting': return 'node-ring-waiting';
      case 'pending': return 'node-ring-pending';
      default: return '';
    }
  }, [data.status]);

  return (
    <div
      className={`
        relative min-w-[260px] max-w-[320px] rounded-2xl overflow-hidden cursor-pointer
        transition-all duration-500 ease-out
        ${animation}
        ${ringClass}
        ${selected ? 'ring-2 ring-brand-green/60 scale-[1.02]' : ''}
      `}
      style={{
        background: 'rgba(255, 255, 255, 0.06)',
        backdropFilter: 'blur(24px) saturate(1.8)',
        WebkitBackdropFilter: 'blur(24px) saturate(1.8)',
        border: `1px solid ${selected ? 'rgba(16, 185, 129, 0.4)' : 'rgba(255, 255, 255, 0.12)'}`,
        boxShadow: `
          inset 0 1px 0 0 rgba(255, 255, 255, 0.15),
          inset 0 -2px 8px 0 rgba(0, 0, 0, 0.2),
          0 8px 32px -4px rgba(0, 0, 0, 0.3)
        `,
      }}
      onDoubleClick={data.onEdit}
    >
      {/* Liquid refraction highlight */}
      <div
        className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none rounded-t-2xl"
        style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 60%, transparent 100%)',
        }}
      />

      {/* Status bar at top */}
      <div
        className="h-1 w-full"
        style={{
          background: `linear-gradient(90deg, ${statusColor} 0%, ${statusColor}88 100%)`,
          boxShadow: `0 0 8px ${statusColor}40`,
        }}
      />

      <div className="p-4 relative z-10">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="text-sm font-semibold text-white leading-tight flex-1 line-clamp-2">
            {data.label}
          </h3>
          <span
            className="shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
            style={{
              background: `${priorityColor}20`,
              color: priorityColor,
              border: `1px solid ${priorityColor}40`,
            }}
          >
            {PRIORITY_LABELS[data.priority]}
          </span>
        </div>

        {/* Description */}
        {data.description && (
          <p className="text-xs text-white/50 mb-3 line-clamp-2">{data.description}</p>
        )}

        {/* Tags */}
        {data.tags && data.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {data.tags.slice(0, 3).map((tag, i) => (
              <span
                key={i}
                className="px-2 py-0.5 rounded-full text-[10px] bg-white/5 text-white/60 border border-white/10"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Status badge */}
            <span
              className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
              style={{
                background: `${statusColor}15`,
                color: statusColor,
                border: `1px solid ${statusColor}30`,
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: statusColor }}
              />
              {NODE_STATUS_LABELS[data.status]}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {/* Due date */}
            {data.dueDate && (
              <span className="flex items-center gap-1 text-[10px] text-white/40">
                <IconClock size={10} />
                {new Date(data.dueDate).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' })}
              </span>
            )}

            {/* Assignee */}
            {data.assigneeName && (
              <div className="flex items-center gap-1">
                {data.assigneeAvatar ? (
                  <img
                    src={data.assigneeAvatar}
                    alt={data.assigneeName}
                    className="w-5 h-5 rounded-full border border-white/20"
                  />
                ) : (
                  <div className="w-5 h-5 rounded-full bg-brand-green/20 border border-brand-green/30 flex items-center justify-center">
                    <IconUser size={10} color="#10b981" />
                  </div>
                )}
                <span className="text-[10px] text-white/50">{data.assigneeName}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Handles */}
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3 !h-3 !bg-brand-green/50 !border-2 !border-brand-green/80"
      />
      <Handle
        type="source"
        position={Position.Right}
        className="!w-3 !h-3 !bg-brand-blue/50 !border-2 !border-brand-blue/80"
      />
    </div>
  );
};

export default memo(OperationalNodeComponent);
